import { animate, AnimationBuilder, AnimationMetadata, AnimationPlayer, style } from '@angular/animations';
import { AfterViewInit, Directive, ElementRef, Inject, OnDestroy, Input, Output, EventEmitter } from '@angular/core';

import { fromEvent, Subject, merge, Observable} from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, share, throttleTime, takeUntil, switchMap, tap, debounceTime } from 'rxjs/operators';
import { debugToConsole } from '../../dev/debug-operator';

// or https://github.com/NetanelBasal/ngx-take-until-destroy/blob/master/src/take-until-destroy.ts
// import { untilDestroy } from '@ngx-starter-kit/ngx-utils';
// import { WINDOW } from '@ngx-starter-kit/core';
import { WindowRef } from '../../services/window.token';

export enum Direction {
  Top = 'Top',
  Up = 'Up',
  Down = 'Down',
}

@Directive({
  /* tslint:disable-next-line */
  selector: '[stickyHeader]',
})
// https://netbasal.com/reactive-sticky-header-in-angular-12dbffb3f1d3
// https://github.com/zetsnotdead/ng-reactive-sticky-header/blob/master/src/app/sticky-header.component.ts
// https://stackoverflow.com/questions/51931366/sticky-header-for-my-table-in-angular-5
// https://codesandbox.io/s/kw7q12q9zr
export class StickyHeaderDirective implements AfterViewInit, OnDestroy {
  player: AnimationPlayer;
  private ngUnsubscribe = new Subject();

  /* tslint:disable-next-line:no-input-rename */
  @Input('customEvent') useCustomEvent = false;

  @Output() onPageYOffsetEvent = new EventEmitter<[number, number, Direction, number, number, boolean]>(); // debug IOS
  @Output() onDirectionEvent = new EventEmitter<Direction>();
  /* tslint:disable-next-line:no-input-rename */
  @Input('customDirectionHandler') customDirectionHandler$: (direction: Observable<Direction>) => Observable<Direction>;

  set show(show: boolean) {
    if (this.useCustomEvent) {
      return;
    }

    if (this.player) {
      this.player.destroy();
    }

    // const metadata = show ? this.fadeIn() : this.fadeOut();
    const metadata = show ? this.fadeUp() : this.fadeDown();

    const factory = this.builder.build(metadata);
    const player = factory.create(this.el.nativeElement);

    player.play();
  }

  constructor(
    private builder: AnimationBuilder,
    private el: ElementRef,
    @Inject(WindowRef) private window: Window
  ) {
 }

  private fadeIn(): AnimationMetadata[] {
    return [style({ opacity: 0 }), animate('200ms ease-in', style({ opacity: 1 }))];
  }

  private fadeOut(): AnimationMetadata[] {
    return [style({ opacity: '*' }), animate('200ms ease-in', style({ opacity: 0 }))];
  }

  private fadeUp(): AnimationMetadata[] {
    return [style({ opacity: 0 }), animate('200ms ease-in', style({ opacity: 1, transform: 'translateY(0)' }))];
  }

  private fadeDown(): AnimationMetadata[] {
    return [style({ opacity: '*' }), animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-100%)' }))];
  }

  // https://gist.github.com/Hyvi/1445943
  private f_clientHeight(): number {
    return this.f_filterResults(
      window.innerHeight ? window.innerHeight : 0,
      document.documentElement ? document.documentElement.clientHeight : 0,
      document.body ? document.body.clientHeight : 0
    );
  }
  private f_filterResults(n_win, n_docel, n_body): number {
    let n_result = n_win ? n_win : 0;
    if (n_docel && (!n_result || (n_result > n_docel))) {
      n_result = n_docel;
    }
    return n_body && (!n_result || (n_result > n_body)) ? n_body : n_result;
  }
  // https://javascript.info/size-and-scroll-window
  private f_scrollHeight(): number {
    return Math.max(
      document.body.scrollHeight, document.documentElement.scrollHeight,
      document.body.offsetHeight, document.documentElement.offsetHeight,
      document.body.clientHeight, document.documentElement.clientHeight
    );
  }


  ngAfterViewInit() {
    const scroll$ = fromEvent<Event>(this.window, 'scroll').pipe(
      //throttleTime(100),
      map(() => {
        // r eturn this.window.pageYOffset;
        return this.window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      }),
      debounceTime(250),
      pairwise(),
      // debugToConsole("[y1, y2]:"),
      map(([y1, y2], index): Direction => {

        let direction: Direction;

        const scrollHeight = this.f_scrollHeight();
        const clientHeight = this.f_clientHeight();
        // https://stackoverflow.com/questions/18614301/keep-overflow-div-scrolled-to-bottom-unless-user-scrolls-up
        // var isScrolledToBottom = document.documentElement.scrollHeight - document.documentElement.clientHeight <= document.documentElement.scrollTop + 1;
        const isScrolledToBottom = scrollHeight - clientHeight <= y2 + 1;

        if (/*y1 == 0 ||*/y2 <= 0) {
          direction = Direction.Top;
        } else if (isScrolledToBottom) {
          direction = Direction.Down;
        } else {
          direction = (y2 < y1 ? Direction.Up : Direction.Down);
        }

        this.onPageYOffsetEvent.emit([y1, y2, direction, clientHeight, scrollHeight, isScrolledToBottom]);

        return direction;

      }),
      distinctUntilChanged(),
      share(),
      // untilDestroy(this),
      takeUntil(this.ngUnsubscribe)
    );

    const goingUp$ = scroll$.pipe(filter(direction => direction === Direction.Up));
    const goingDown$ = scroll$.pipe(filter(direction => direction === Direction.Down));
    const top$ = scroll$.pipe(filter(direction => direction === Direction.Top));

    // this.onStickyHeaderEvent =
    const mergedUpDown = merge(goingUp$, goingDown$, top$)
      .pipe(
        // debugToConsole("merge: goingUp$, goingDown$; Direction:"),
        this.customDirectionHandler$, // outer event stream // ToDo V: should be improves, throw ex if not defined
        tap((event) => {
          if (!this.customDirectionHandler$) {

            this.onDirectionEvent.emit(event); // outer event value
            this.show = event === Direction.Up || event === Direction.Top; // inner event
          }
        }),
        takeUntil(this.ngUnsubscribe),
    );

    mergedUpDown.subscribe();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
